import { useState } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import {
  Sparkle24Regular,
  ArrowClockwise24Regular,
  Lightbulb24Regular,
  Warning24Regular,
  Checkmark24Regular,
} from '@fluentui/react-icons';
import { Card } from '@/components/common/Card';
import { openAIService } from '@/services/openai';

interface AIOverviewProps {
  projectData: {
    name: string;
    location: string;
    leveredIRR: number;
    equityMultiple: number;
    totalCost: number;
    stabilizedNOI: number;
    yieldOnCost: number;
    exitCapRate: number;
  };
  tenantData?: {
    totalTenants: number;
    totalNRA: number;
    categories: string[];
  };
}

interface OverviewContent {
  summary: string;
  keyFindings: string[];
  risks: string[];
  opportunities: string[];
  recommendations: string[];
}

export function AIOverview({ projectData, tenantData }: AIOverviewProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [overview, setOverview] = useState<OverviewContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  const generateOverview = async () => {
    if (!openAIService.hasApiKey()) {
      setError('Please configure your OpenAI API key in Settings to generate AI insights.');
      return;
    }

    setIsLoading(true);
    setError(null);

    const prompt = `As a real estate development analyst, provide a comprehensive overview of this project:

Project: ${projectData.name}
Location: ${projectData.location}
Total Project Cost: $${(projectData.totalCost / 1000000).toFixed(1)}M
Stabilized NOI: $${(projectData.stabilizedNOI / 1000000).toFixed(2)}M
Levered IRR: ${(projectData.leveredIRR * 100).toFixed(2)}%
Equity Multiple: ${projectData.equityMultiple.toFixed(2)}x
Yield on Cost: ${(projectData.yieldOnCost * 100).toFixed(2)}%
Exit Cap Rate: ${(projectData.exitCapRate * 100).toFixed(2)}%
${tenantData ? `\nTenant Mix: ${tenantData.totalTenants} tenants across ${tenantData.totalNRA.toLocaleString()} SF\nCategories: ${tenantData.categories.join(', ')}` : ''}

Provide analysis in this EXACT JSON format (no code blocks, plain JSON only):

{
  "summary": "2-3 sentence executive summary of the project's financial viability",
  "keyFindings": [
    "Finding 1 about returns",
    "Finding 2 about costs or structure",
    "Finding 3 about market positioning"
  ],
  "risks": [
    "Risk 1 with specific concern",
    "Risk 2 with specific concern"
  ],
  "opportunities": [
    "Opportunity 1 for value creation",
    "Opportunity 2 for optimization"
  ],
  "recommendations": [
    "Recommendation 1 with actionable advice",
    "Recommendation 2 with actionable advice"
  ]
}

Requirements:
- Be specific and quantitative
- Reference actual metrics from the data
- Keep bullet points concise (1-2 sentences each)
- Focus on actionable insights
- Return ONLY the JSON, no additional text`;

    try {
      const response = await openAIService.chatCompletion(
        [
          {
            role: 'system',
            content:
              'You are an expert real estate development analyst. Provide detailed, data-driven insights in valid JSON format only.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        {
          model: 'gpt-4o-mini',
          temperature: 0.7,
          maxTokens: 1500,
        }
      );

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      // Parse JSON response
      try {
        const cleanedContent = response.content
          .replace(/```json\n?/g, '')
          .replace(/```\n?/g, '')
          .trim();
        const parsed = JSON.parse(cleanedContent);
        setOverview(parsed);
      } catch (e) {
        console.error('Failed to parse AI response:', response.content);
        setError('Failed to parse AI response. Please try again.');
      }
    } catch (e) {
      setError('An unexpected error occurred. Please try again.');
    }

    setIsLoading(false);
  };

  return (
    <Card className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkle24Regular className="text-salesforce-blue" />
          <h2 className="text-xl font-semibold">AI Project Overview</h2>
        </div>
        <Button
          appearance="primary"
          icon={overview ? <ArrowClockwise24Regular /> : <Sparkle24Regular />}
          onClick={generateOverview}
          disabled={isLoading}
        >
          {overview ? 'Regenerate' : 'Generate'} Overview
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="large" label="Analyzing project data..." />
        </div>
      )}

      {error && (
        <div className="bg-error-red/10 border border-error-red/30 rounded p-4 text-sm text-error-red">
          {error}
        </div>
      )}

      {!isLoading && !error && !overview && (
        <div className="text-center py-12 text-gray-300">
          <Sparkle24Regular className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">
            Click "Generate Overview" to get AI-powered insights about this project
          </p>
        </div>
      )}

      {overview && !isLoading && (
        <div className="space-y-6">
          {/* Executive Summary */}
          <div className="bg-salesforce-blue/5 border-l-4 border-salesforce-blue rounded p-4">
            <p className="text-sm leading-relaxed">{overview.summary}</p>
          </div>

          {/* Key Findings */}
          {overview.keyFindings && overview.keyFindings.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Checkmark24Regular className="text-success-green" />
                Key Findings
              </h3>
              <ul className="space-y-2">
                {overview.keyFindings.map((finding, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-success-green mt-0.5">✓</span>
                    <span>{finding}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {overview.risks && overview.risks.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Warning24Regular className="text-warning-orange" />
                Risks to Consider
              </h3>
              <ul className="space-y-2">
                {overview.risks.map((risk, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-warning-orange mt-0.5">⚠</span>
                    <span>{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Opportunities */}
          {overview.opportunities && overview.opportunities.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Lightbulb24Regular className="text-electric-blue" />
                Opportunities
              </h3>
              <ul className="space-y-2">
                {overview.opportunities.map((opp, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-electric-blue mt-0.5">💡</span>
                    <span>{opp}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {overview.recommendations && overview.recommendations.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Sparkle24Regular className="text-salesforce-blue" />
                Recommendations
              </h3>
              <ul className="space-y-2">
                {overview.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <span className="text-salesforce-blue mt-0.5">→</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
